import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, Subject, firstValueFrom, of, switchMap, tap } from 'rxjs';
import { AccountGetRefreshTokenCommand, ProfileLiteView } from 'src/app/api/models';
import { AccountsService } from 'src/app/api/services';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private router = inject(Router);
  private jwtHelper = inject(JwtHelperService);
  private accountsService = inject(AccountsService);


  private access_token_name: string = 'access_token';
  private refresh_token_name: string = 'refresh_token';
  private refresh_token_expiry_name: string = 'refresh_token_expiry';

  private isAuthenticating = false;

  private authChangeSub = new Subject<boolean>()
  public authChanged = this.authChangeSub.asObservable();

  constructor() {
    this.isAuthenticated().pipe(tap(result => this.authChangeSub.next(result)));
  }

  getAccessToken = (): string => localStorage.getItem(this.access_token_name)?.toString()!;
  getRefreshToken = (): string => localStorage.getItem(this.refresh_token_name)?.toString()!;
  getRefreshTokenExpiry = (): string => localStorage.getItem(this.refresh_token_expiry_name)?.toString()!;

  public login = (access_token: string, refresh_token: string, refresh_token_expiry: string, isAuthenticated: boolean, returnUrl?: string) => {
    this.setToken(access_token, refresh_token, refresh_token_expiry);
    this.authChangeSub.next(isAuthenticated);
    this.router.navigate([returnUrl]);
  }

  public logout = () => {
    localStorage.clear();
    this.authChangeSub.next(false);
    this.router.navigate(['/']);
  }

  public setToken = (token: string, refreshToken: string, refreshTokenExpiresOn: string) => {
    localStorage.setItem(this.access_token_name, token!);
    localStorage.setItem(this.refresh_token_name, refreshToken!);
    localStorage.setItem(this.refresh_token_expiry_name, refreshTokenExpiresOn!);
  }

  public forceRefresh = () => {
    this.authChangeSub.next(true);
  }

  public isAuthenticated(): Observable<boolean> {

    this.isAuthenticating = true;

    let token = this.getAccessToken();

    if (!!token && !this.jwtHelper.isTokenExpired(token)) {
      this.isAuthenticating = false;
      return of(true);
    };

    const refreshToken = this.getRefreshToken()
    const refreshTokenExpiresOn = this.getRefreshTokenExpiry();
    if (!!refreshToken && new Date(refreshTokenExpiresOn!) > new Date()) {
      return this.doRefresh();
    }
    this.isAuthenticating = false;
    return of(false);
  }

  sleep = (milliseconds: number) => {
    var start = new Date().getTime();
    for (var i = 0; i < 1e7; i++) {
      if ((new Date().getTime() - start) > milliseconds) {
        break;
      }
    }
  }

  doRefresh(): Observable<boolean> {
    const p: AccountGetRefreshTokenCommand = {
      refreshToken: this.getRefreshToken()!,
      accessToken: this.getAccessToken()
    };
    
    if(!!p.refreshToken){
      return of(false);
    }

    this.isAuthenticating = true;
    return this.accountsService.getRefreshToken({ body: p }).pipe(
      switchMap(res => {
        if (res.isSuccessful) {
          this.setToken(res.accessToken!, res.refreshToken!, res.refreshTokenExpiresOn!);
          this.isAuthenticating = false;
          return of(true);
        }
        this.isAuthenticating = false;
        return of(false);
      })
    );
  }

  public async checkAuth() {
    const isAuth = await firstValueFrom(this.isAuthenticated());
    if (isAuth) {
      this.authChangeSub.next(true);
    }
  }

  private createCompleteRoute = (route: string, envAddress: string) => `${envAddress}/${route}`;

  public isUserAdmin = (): boolean => this.isInRole('Administrator');
  
  public isUserLandlord = (): boolean => this.isInRole('Landlord');
  public isUserSales = (): boolean => this.isInRole('Sales');

  public isUserPro = (): Observable<boolean>  => {
    return this.accountsService.isPro().pipe();
  }

  isInRole = (role: string): boolean => {
    const token = this.getAccessToken();
    if (!token || this.jwtHelper.isTokenExpired(token)) return false;
    const decodedToken = this.jwtHelper.decodeToken(token);
    const roles: string[] = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    return !!roles && roles.indexOf(role) > -1;
  }

  profile = (): ProfileLiteView => {
    const token = this.getAccessToken();
    if (!token || this.jwtHelper.isTokenExpired(token)) return {};
    const decodedToken = this.jwtHelper.decodeToken(token);
    return {
      id: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid'],
      email: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
      name: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
      isLandlord: this.isUserLandlord()
    };
  }

  userName = (): string => {
    let token = this.getAccessToken();

    if (!token || this.jwtHelper.isTokenExpired(token)) return 'UNKNOWN';

    const decodedToken = this.jwtHelper.decodeToken(token);
    const names: string[] = decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name']
    return names.toString();
  }

}
